import React from 'react';
import {
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  Alert,
} from '@mui/material';
import { Add as AddIcon, TextFields as TextIcon } from '@mui/icons-material';
import BaseModal from '../BaseModal';
import { CustomButton } from '../../Infra/CustomButton/CustomButton';
import { MultiColumnMatchingModalProps } from './types';
import { useMultiColumnMatching } from './useMultiColumnMatching';

const MultiColumnMatchingModal: React.FC<MultiColumnMatchingModalProps> = ({
  open,
  onClose,
  onSave,
  initialRules = [],
  fieldName,

  column1Name = 'Column 1',
  column2Name = 'Column 2',
}) => {
  const {
    rules,
    getInputValue,
    setInputValue,
    addValueToColumn,
    removeValueFromColumn,
    handleKeyPress,
    handlePaste,
    getValidRules,
    hasValidRules,
  } = useMultiColumnMatching(open, initialRules, fieldName);

  const handleSave = () => {
    const validRules = getValidRules();
    onSave(validRules);
    onClose();
  };

  return (
    <BaseModal
      dataTestId="multi-column-matching-modal"
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={`Multi-Column Configuration - ${fieldName}`}
      icon={<TextIcon />}
      submitText="Save Configuration"
      submitDisabled={!hasValidRules}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            How Multi-Column Matching Works
          </Typography>
          <Typography variant="body2">
            Configure values for each column. When BOTH columns contain ANY of
            the specified values in their respective lists, the row will be
            classified accordingly.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            {`Example: If ${column1Name} contains "Credit" AND ${column2Name} contains "IN", the transaction will be classified as "Deposit".`}
          </Typography>
        </Alert>

        {rules.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No configuration found
            </Typography>
          </Box>
        ) : (
          <Stack spacing={4}>
            {rules.map((rule, ruleIndex) => (
              <Paper
                key={ruleIndex}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'white',
                }}
                variant="outlined"
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {rule.standardValue}
                  </Typography>
                </Box>

                <Stack spacing={3}>
                  {/* Column 1 Section */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {column1Name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        size="small"
                        placeholder={`Add value for ${column1Name} (Tip: Paste multiple Excel cells)`}
                        value={getInputValue(rule.standardValue, 'column1')}
                        onChange={e =>
                          setInputValue(
                            rule.standardValue,
                            'column1',
                            e.target.value
                          )
                        }
                        onKeyPress={e =>
                          handleKeyPress(e, ruleIndex, 'column1')
                        }
                        onPaste={e => handlePaste(e, ruleIndex, 'column1')}
                        sx={{ flexGrow: 1 }}
                        helperText={
                          getInputValue(rule.standardValue, 'column1') &&
                          rule.column1Values &&
                          rule.column1Values.includes(
                            getInputValue(rule.standardValue, 'column1').trim()
                          )
                            ? `"${getInputValue(rule.standardValue, 'column1').trim()}" already exists`
                            : undefined
                        }
                        error={
                          !!getInputValue(rule.standardValue, 'column1') &&
                          rule.column1Values &&
                          rule.column1Values.includes(
                            getInputValue(rule.standardValue, 'column1').trim()
                          )
                        }
                      />
                      <CustomButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          addValueToColumn(
                            ruleIndex,
                            'column1',
                            getInputValue(rule.standardValue, 'column1')
                          );
                        }}
                        size="small"
                        disabled={
                          !getInputValue(
                            rule.standardValue,
                            'column1'
                          )?.trim() ||
                          (rule.column1Values &&
                            rule.column1Values.includes(
                              getInputValue(
                                rule.standardValue,
                                'column1'
                              ).trim()
                            ))
                        }
                      >
                        Add
                      </CustomButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rule.column1Values &&
                        rule.column1Values.map((value, valueIndex) => (
                          <Chip
                            key={valueIndex}
                            label={value}
                            onDelete={() =>
                              removeValueFromColumn(
                                ruleIndex,
                                'column1',
                                valueIndex
                              )
                            }
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              color: 'grey.800',
                              borderColor: 'grey.300',
                              '&:hover': {
                                bgcolor: 'grey.200',
                              },
                              '& .MuiChip-deleteIcon': {
                                color: 'grey.600',
                                '&:hover': {
                                  color: 'grey.800',
                                },
                              },
                            }}
                            variant="outlined"
                          />
                        ))}
                    </Box>
                  </Box>

                  {/* Column 2 Section */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {column2Name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        size="small"
                        placeholder={`Add value for ${column2Name} (Tip: Paste multiple Excel cells)`}
                        value={getInputValue(rule.standardValue, 'column2')}
                        onChange={e =>
                          setInputValue(
                            rule.standardValue,
                            'column2',
                            e.target.value
                          )
                        }
                        onKeyPress={e =>
                          handleKeyPress(e, ruleIndex, 'column2')
                        }
                        onPaste={e => handlePaste(e, ruleIndex, 'column2')}
                        sx={{ flexGrow: 1 }}
                        helperText={
                          getInputValue(rule.standardValue, 'column2') &&
                          rule.column2Values &&
                          rule.column2Values.includes(
                            getInputValue(rule.standardValue, 'column2').trim()
                          )
                            ? `"${getInputValue(rule.standardValue, 'column2').trim()}" already exists`
                            : undefined
                        }
                        error={
                          !!getInputValue(rule.standardValue, 'column2') &&
                          rule.column2Values &&
                          rule.column2Values.includes(
                            getInputValue(rule.standardValue, 'column2').trim()
                          )
                        }
                      />
                      <CustomButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          addValueToColumn(
                            ruleIndex,
                            'column2',
                            getInputValue(rule.standardValue, 'column2')
                          );
                        }}
                        size="small"
                        disabled={
                          !getInputValue(
                            rule.standardValue,
                            'column2'
                          )?.trim() ||
                          (rule.column2Values &&
                            rule.column2Values.includes(
                              getInputValue(
                                rule.standardValue,
                                'column2'
                              ).trim()
                            ))
                        }
                      >
                        Add
                      </CustomButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rule.column2Values &&
                        rule.column2Values.map((value, valueIndex) => (
                          <Chip
                            key={valueIndex}
                            label={value}
                            onDelete={() =>
                              removeValueFromColumn(
                                ruleIndex,
                                'column2',
                                valueIndex
                              )
                            }
                            size="small"
                            sx={{
                              bgcolor: 'grey.100',
                              color: 'grey.800',
                              borderColor: 'grey.300',
                              '&:hover': {
                                bgcolor: 'grey.200',
                              },
                              '& .MuiChip-deleteIcon': {
                                color: 'grey.600',
                                '&:hover': {
                                  color: 'grey.800',
                                },
                              },
                            }}
                            variant="outlined"
                          />
                        ))}
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </BaseModal>
  );
};

export default MultiColumnMatchingModal;
